import React, {useState, useEffect, useRef} from 'react';
import './EstimatePage.css';
import {
  CircularProgress,
  Fab,
  Modal, SpeedDial, SpeedDialAction, Tooltip,
} from "@mui/material";
import {Add as AddIcon, Fence, LocalShipping, LocationSearching, MyLocation} from "@mui/icons-material";
import truckChipperIcon from '../../assets/truck_chipper_icon.png';
import personIcon from '../../assets/person_icon.png';
import fallenTreePin from '../../assets/fallen_tree_pin.png';
import fallenTreeIcon from '../../assets/fallen_tree_icon.png';
import treeGroupPin from '../../assets/tree_group_pin.png';
import treeGroupIcon from '../../assets/tree_group_icon.png';
import treePin from '../../assets/tree_pin.png';
import treeIcon from '../../assets/tree_icon.png';
import stumpPin from '../../assets/stump_pin.png';
import stumpIcon from '../../assets/stump_icon.png';
import miscellaneousPin from '../../assets/miscellaneous_pin.png';
import miscellaneousIcon from '../../assets/miscellaneous_icon.png';
import debrisPilePin from '../../assets/debris_pile_pin.png';
import debrisPileIcon from '../../assets/debris_pile_icon.png';
import {useNavigate} from "react-router-dom";
import {createTree, updateTree} from "./estimate-tree.service";
import {EstimateTreeDto, WorkItemType} from "../../shared/dtos/estimate-tree.dto";
import {EstimateWithComponentsDto} from "../../shared/dtos/estimate-with-components.dto";
import EstimateTree from "./estimate-tree-detail/EstimateTreeDetailComponent";
import {enqueueSnackbar} from "notistack";
import {logError} from "../../shared/services/logger.service";
import {googleMapsLoader} from "../../shared/config/google-maps";
import MapImageIcon from "../../shared/components/MapImageIcon";
import {renderReactNode} from "../../shared/services/dom-helper.service";
import {createGate, updateGate} from "./estimate-gate.service";
import {EstimateGateDetailsDto, EstimateGateDto} from "../../shared/dtos/estimate-gate.dto";
import EstimateGateDetail from "./EstimateGateDetailComponent";
import {createAdditionalComponent, updateAdditionalComponent} from "./estimate-additional-component.service";
import {EstimateAdditionalComponentDto} from "../../shared/dtos/estimate-additional-component.dto";
import {AddEstimateDragPathDto} from "../../shared/dtos/estimate-drag-path.dto";
import {createDragPath, deleteDragPath, updateDragPath} from "./estimate-drag-path.service";
import { MarkerInfo } from '../../shared/misc/marker-info.interface';
import {MarkableElement} from "../../shared/misc/markable-element";
import {syncMarkers} from "../../shared/services/maps-helper.service";
import EstimateInfoSidebar from "./EstimateInfoSidebar";
import {EstimateStatusUpdateMenu} from "../../shared/components/EstimateStatusUpdateMenu";
import {ExternalCompanyDto} from "../../shared/dtos/company.dto";
import EstimateChipperTruckDetailComponent from "./EstimateChipperTruckDetailComponent";
import {getDragPathUpdate, getPathDistanceFeet, getChipperRearCenterAnchor, getChipperIconLengthPixels, getChipperIconScale, CHIPPER_ICON_WIDTH_TO_LENGTH_RATIO} from "./estimate-page-helpers";
import { replaceItemInArray } from '../../shared/misc/react-state-utils';
import {useLocationPermissions} from "../../components/LocationPermissionsContext";
import {nextOpenLocation} from "../../shared/misc/location-helpers";
import {buildSpiderfyClusters, buildSpiderfyDisplayMarkers, SpiderfyDisplayMarker, defaultLocationTipFromMarker, SPIDERFY_MARKER_DIAMETER_PIXELS, SPIDERFY_MARKER_PIN_HEIGHT_PIXELS, resolveDisplaysAroundDrag, commitDisplaysAfterDrag, displaysMatchTreeTrues, nudgeDisplaysClearOfOverlaps, restDisplayForTrueLocation} from "../../shared/misc/spiderfy-helpers";


export interface EstimateMapWorkspaceComponentProps {
  estimate: EstimateWithComponentsDto;
  setEstimate: React.Dispatch<(prevState: EstimateWithComponentsDto) => EstimateWithComponentsDto>;
  company: ExternalCompanyDto;
  urls: {
    getBaseUrl: () => string;
    getSelectedTreeUrl: (treeId: number) => string;
  }
  initialTreeId?: number;
  mode?: 'edit' | 'read-only';
  sidebarEnabled?: boolean;
  /** False hides the add menu (SQ-500 library preparation corrects existing work only). */
  addingEnabled?: boolean;
  /** Library preparation asks the map to act on a truck & chipper from outside: add one, pan to one, or open its details (the first one when no truckId). */
  truckRequest?: {id: number, action: 'add' | 'show' | 'edit', truckId?: number};
  /** False: clicking the truck does not open its dialog (library preparation opens it from its own panel), so it can be dragged and turned freely. */
  truckDialogOnClick?: boolean;
  /** When set, clicking a work item selects it in the caller instead of opening the detail modal. */
  onWorkItemSelect?: (treeId: number) => void;
}

interface DragPathInfo {
  pathId: number,
  path: google.maps.Polyline,
}

const DEFAULT_ZOOM_LEVEL = 8;
const ICON_STANDARD_ZOOM_LEVEL = 21;
const POSITION_UPDATE_INTERVAL_MS = 10000;
// Passing a null delay pauses the interval (e.g. when we lack location permission).
const useInterval = (callback: () => void, delay: number | null) => {
  useEffect(() => {
    if (delay === null) return;
    const intervalId = setInterval(callback, delay);

    return () => clearInterval(intervalId);
  }, [callback, delay]);
};

// Make sure this matches the one in EstimateTreeDetailComponent.tsx
const getUnsavedModificationsLocalStorageKey = (estimateId: number, treeId: number) => `unsavedTree_${estimateId}_${treeId}`;

const EstimateMapWorkspaceComponent = ({estimate, setEstimate, company, urls, initialTreeId, mode: initialMode, sidebarEnabled: initialSidebarEnabled, addingEnabled = true, truckRequest, truckDialogOnClick = true, onWorkItemSelect}: EstimateMapWorkspaceComponentProps) => {
  const mode = initialMode ?? 'edit';
  
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const canEdit = mode === 'edit';
  const sidebarEnabled = initialSidebarEnabled ?? true;
  const {state: locationState, hasLocationPermission, requestLocation} = useLocationPermissions();

  const [pageEssentialsLoaded, setPageEssentialsLoaded] = useState<boolean>(false);
  const [createItemIsLoading, setCreateItemIsLoading] = useState<boolean>(false);
  const [userLocation, setUserLocation] = useState<{lat: number; lng: number} | null>(null);
  const [centerOnUser, setCenterOnUser] = useState<boolean>(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapScale, setMapScale] = useState(1);
  const [userMarker, setUserMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);

  const [selectedTree, setSelectedTree] = useState<EstimateTreeDto>();
  const [treeMarkers, setTreeMarkers] = useState<MarkerInfo[]>([]);
  const treeMarkersRef = useRef<MarkerInfo[]>([]);
  const [treeIdToLocalIdMap, setTreeIdToLocalIdMap] = useState<{[key: number]: number}>({});
  const spiderLinesRef = useRef<google.maps.Polyline[]>([]);
  const spiderLineByIdRef = useRef<Map<number, google.maps.Polyline>>(new Map());
  const restDisplaysRef = useRef<SpiderfyDisplayMarker[]>([]);
  /** Snapshot at drag start — nudges are computed from this each frame. */
  const dragBaselineRef = useRef<SpiderfyDisplayMarker[] | null>(null);
  /** Tree id currently being dragged (null when idle). */
  const draggingTreeIdRef = useRef<number | null>(null);
  /** Last layout from the in-progress (or just-finished) drag. */
  const lastDragDisplaysRef = useRef<SpiderfyDisplayMarker[] | null>(null);
  /** Prefer this layout over a full spiderfy rebuild while trues still match. */
  const preservedDisplaysRef = useRef<SpiderfyDisplayMarker[] | null>(null);
  /** Zoom level the preserved layout was built for (pixel geometry is zoom-specific). */
  const preservedZoomRef = useRef<number | null>(null);
  /** Keep drop release center fixed through the next sync after drag end. */
  const postDropFrozenTreeIdRef = useRef<number | null>(null);

  const [selectedGate, setSelectedGate] = useState<EstimateGateDto>();
  const [gateMarkers, setGateMarkers] = useState<MarkerInfo[]>([]);
  const gateMarkersRef = useRef<MarkerInfo[]>([]);
  const [gateIdToLocalIdMap, setGateIdToLocalIdMap] = useState<{[key: number]: number}>({});

  const [selectedChipperTruck, setSelectedChipperTruck] = useState<EstimateAdditionalComponentDto>();
  const [additionalComponentMarkers, setAdditionalComponentMarkers] = useState<MarkerInfo[]>([]);
  const additionalComponentMarkersRef = useRef<MarkerInfo[]>([]);
  const [chipperIdToLocalIdMap, setChipperIdToLocalIdMap] = useState<{[key: number]: number}>({});
  const [dragPathsObjects, setDragPathsObjects] = useState<DragPathInfo[]>([]);
  const [updateStatusMenuLastClick] = useState<React.MouseEvent<HTMLButtonElement> | null>(null);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  const changedEstimateRef = useRef(estimate);

  useEffect(() => {
    changedEstimateRef.current = estimate;
  }, [estimate]);

  const getMaxZoom = (lat: number, lng: number) => {
    const latLng = new window.google.maps.LatLng(lat, lng);
    const maxZoomService = new window.google.maps.MaxZoomService();

    return new Promise<number>((resolve, reject) => {
      maxZoomService.getMaxZoomAtLatLng(latLng, response => {
        if (response.status === 'OK') {
          resolve(response.zoom);
        } else {
          logError('Max zoom not found for this location', {lat: estimate.lat, lng: estimate.lng})
          resolve(DEFAULT_ZOOM_LEVEL);
        }
      }).catch(e => {
        logError('Error getting max zoom', {lat: estimate.lat, lng: estimate.lng}, e)
        resolve(DEFAULT_ZOOM_LEVEL);
      });
    })
  }

  const buildElementIdToLocalIdMap = (elements: MarkableElement[]) => {
    const elementIdToLocalIdMap: {[key: number]: number} = {};
    elements
      .map(e => e.id)
      .sort((a, b) => a - b)
      .forEach((id, i) => {
        elementIdToLocalIdMap[id] = i + 1; // Start the ids at 1 for the user
      });
    return elementIdToLocalIdMap;
  }

  const bumpMarkerZIndexes = () => {
    // We want the markers to be drawn over the drag paths
    const markers = [...treeMarkers, ...gateMarkers, ...additionalComponentMarkers];
    markers.forEach(tm => {
      const currentIndex = tm.markerDomObject.zIndex ?? 0;
      tm.markerDomObject.zIndex = (currentIndex + 1) % 10;
    })
  }

  const onAdditionComponentMarkerClick = (additionalComponent: EstimateAdditionalComponentDto) => {
    if(additionalComponent.type === 'chipper' && truckDialogOnClick) {
      setSelectedChipperTruck(additionalComponent);
    }
  }

  const clearSpiderLines = () => {
    spiderLinesRef.current.forEach(line => line.setMap(null));
    spiderLinesRef.current = [];
    spiderLineByIdRef.current = new Map();
  };

  const syncSpiderLines = (displayMarkers: SpiderfyDisplayMarker[], mapInstance: google.maps.Map) => {
    clearSpiderLines();
    const arrowIcon: google.maps.Symbol = {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 1.5,
      strokeColor: '#ff0000',
      fillColor: '#ff0000',
      fillOpacity: 1,
    };
    const byId = new Map<number, google.maps.Polyline>();
    spiderLinesRef.current = displayMarkers.map(d => {
      const line = new google.maps.Polyline({
        // Base on marker edge; tip marks the true location.
        path: [
          {lat: d.baseLat, lng: d.baseLng},
          {lat: d.tipLat, lng: d.tipLng},
        ],
        geodesic: true,
        strokeColor: '#ff0000',
        strokeOpacity: 0.85,
        strokeWeight: 2,
        icons: [{icon: arrowIcon, offset: '100%'}],
        map: mapInstance,
        clickable: false,
        zIndex: 1,
      });
      byId.set(d.id, line);
      return line;
    });
    spiderLineByIdRef.current = byId;
  };

  const updateSpiderLineDuringDrag = (treeId: number, markerLat: number, markerLng: number) => {
    if (!map) return;
    if (!dragBaselineRef.current) {
      dragBaselineRef.current = restDisplaysRef.current.map(d => ({...d}));
      draggingTreeIdRef.current = treeId;
    }
    const markerCenter = {lat: markerLat, lng: markerLng};
    const displaced = resolveDisplaysAroundDrag(
      dragBaselineRef.current,
      treeId,
      markerCenter,
      map
    );
    lastDragDisplaysRef.current = displaced;

    for (const d of displaced) {
      const line = spiderLineByIdRef.current.get(d.id);
      if (line) {
        line.setPath([
          {lat: d.baseLat, lng: d.baseLng},
          {lat: d.tipLat, lng: d.tipLng},
        ]);
      }

      if (d.id === treeId) continue;
      const markerInfo = treeMarkersRef.current.find(m => m.elementId === d.id);
      if (markerInfo?.markerDomObject) {
        markerInfo.markerDomObject.position = {lat: d.lat, lng: d.lng};
      }
    }
  };

  const readMarkerLatLng = (
    position: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined
  ): {lat: number; lng: number} | null => {
    if (!position) return null;
    if (typeof (position as google.maps.LatLng).lat === 'function') {
      const ll = position as google.maps.LatLng;
      return {lat: ll.lat(), lng: ll.lng()};
    }
    const lit = position as google.maps.LatLngLiteral;
    if (lit.lat == null || lit.lng == null) return null;
    return {lat: lit.lat, lng: lit.lng};
  };

  const syncTreeMarkers = () => {
    if(!map || !estimate.trees) return;

    const treeIdToLocalIdMap = buildElementIdToLocalIdMap(estimate.trees);
    setTreeIdToLocalIdMap(treeIdToLocalIdMap);

    const getIconFromWorkItemType = (workItemType: WorkItemType) => {
      switch(workItemType) {
        case WorkItemType.Tree:
          return treePin;
        case WorkItemType.TreeGroup:
          return treeGroupPin;
        case WorkItemType.FallenTree:
          return fallenTreePin;
        case WorkItemType.Stump:
          return stumpPin;
        case WorkItemType.DebrisPile:
          return debrisPilePin;
        case WorkItemType.Misc:
          return miscellaneousPin;
        default:
          return treePin;
      }
    };

    const clusters = buildSpiderfyClusters(estimate.trees, map);
    const rebuilt = buildSpiderfyDisplayMarkers(clusters, map);
    const preserved = preservedDisplaysRef.current;
    const zoom = map.getZoom() ?? null;
    // Pixel leader/base geometry is zoom-specific — don't reuse across zooms.
    const canReusePreserved =
      !!preserved &&
      zoom != null &&
      preservedZoomRef.current === zoom &&
      displaysMatchTreeTrues(preserved, estimate.trees);
    // Always clear circle/shaft overlaps, including when reusing a drag layout.
    const frozenForNudge = postDropFrozenTreeIdRef.current ?? undefined;
    postDropFrozenTreeIdRef.current = null;
    const displayMarkers = nudgeDisplaysClearOfOverlaps(
      canReusePreserved ? preserved : rebuilt,
      map,
      frozenForNudge
    );
    preservedDisplaysRef.current = displayMarkers;
    preservedZoomRef.current = zoom;
    restDisplaysRef.current = displayMarkers;

    // Mid-drag sync (zoom / data): rest layout changed, so the drag-start baseline
    // is stale. Rebaseline and re-apply the current finger position so the dragged
    // marker doesn't snap back and neighbor nudges match the new geometry.
    let markersToPlace = displayMarkers;
    const draggingId = draggingTreeIdRef.current;
    if (draggingId != null && dragBaselineRef.current) {
      if (!displayMarkers.some(d => d.id === draggingId)) {
        dragBaselineRef.current = null;
        draggingTreeIdRef.current = null;
        lastDragDisplaysRef.current = null;
      } else {
        dragBaselineRef.current = displayMarkers.map(d => ({...d}));
        // Prefer the live DOM position — lastDragDisplaysRef can lag a frame
        // behind the finger and would yank the pin backward mid-drag.
        const livePos = readMarkerLatLng(
          treeMarkersRef.current.find(m => m.elementId === draggingId)?.markerDomObject.position
        );
        const fromLastDrag = lastDragDisplaysRef.current?.find(d => d.id === draggingId);
        const dragCenter = livePos
          ?? (fromLastDrag ? {lat: fromLastDrag.lat, lng: fromLastDrag.lng} : null);
        if (dragCenter) {
          markersToPlace = resolveDisplaysAroundDrag(
            dragBaselineRef.current,
            draggingId,
            dragCenter,
            map
          );
          lastDragDisplaysRef.current = markersToPlace;
        }
      }
    }

    const displayById = new Map(markersToPlace.map(d => [d.id, d]));
    const treesById = new Map(estimate.trees.map(t => [t.id, t]));

    // Pass display lat/lng into sync while keeping true coords on the original tree DTOs.
    const elementsForSync = markersToPlace.map(d => {
      const tree = treesById.get(d.id)!;
      return {...tree, lat: d.lat, lng: d.lng};
    });

    syncSpiderLines(markersToPlace, map);

    syncMarkers(
      document,
      map,
      mapScale,
      'tree',
      elementsForSync,
      treeMarkersRef,
      setTreeMarkers,
      tree => {
        const display = displayById.get(tree.id);
        return display?.isSpiderfied
          ? `spider:${treeIdToLocalIdMap[tree.id]}:${display.lat}:${display.lng}`
          : `${treeIdToLocalIdMap[tree.id]}`;
      },
      tree => (
        <MapImageIcon
          id={`test-tree-marker-id-${tree.id}`}
          src={getIconFromWorkItemType(tree.workItemType)}
          sizeX={SPIDERFY_MARKER_DIAMETER_PIXELS}
          sizeY={SPIDERFY_MARKER_PIN_HEIGHT_PIXELS}
          scale={1}
          label={treeIdToLocalIdMap[tree.id].toString()}
          circular
          centerImage
        />
      ),
      (tree) => {
        handleTreeClick(tree.id);
      },
      (tree, event) => {
        const markerLat = event.latLng?.lat();
        const markerLng = event.latLng?.lng();
        if (markerLat == null || markerLng == null) return;
        const trueTree = treesById.get(tree.id) ?? tree;
        // Tip marks the saved location — convert dropped marker center to the tip
        // south of it (same in spiderfy and default) so the pin doesn't jump.
        const tip = defaultLocationTipFromMarker({lat: markerLat, lng: markerLng}, map);
        const dragLayout = lastDragDisplaysRef.current;
        if (dragLayout) {
          const committed = commitDisplaysAfterDrag(
            dragLayout,
            tree.id,
            {lat: markerLat, lng: markerLng},
            map
          );
          // Drag-time nudges are transient: return neighbors to their default
          // rest geometry so displacement can't accumulate across drags.
          const rested = committed.map(d =>
            d.id === tree.id ? d : restDisplayForTrueLocation(d, map)
          );
          // Freeze the dropped tree so clearance nudges neighbors only — otherwise
          // the pin can jump away from the release center while the tip is saved
          // from that original center via updateTreeLocation below.
          const cleared = nudgeDisplaysClearOfOverlaps(rested, map, tree.id);
          preservedDisplaysRef.current = cleared;
          preservedZoomRef.current = map.getZoom() ?? null;
          restDisplaysRef.current = cleared;
          postDropFrozenTreeIdRef.current = tree.id;
        }
        dragBaselineRef.current = null;
        draggingTreeIdRef.current = null;
        lastDragDisplaysRef.current = null;
        updateTreeLocation(trueTree, tip.lat, tip.lng);
      },
      {gmpDraggable: canEdit},
      (tree, event) => {
        const markerLat = event.latLng?.lat();
        const markerLng = event.latLng?.lng();
        if (markerLat == null || markerLng == null) return;
        updateSpiderLineDuringDrag(tree.id, markerLat, markerLng);
      },
      // The pin's art is the work item's type, which the library can change in place (SQ-671).
      tree => tree.workItemType
    )
  }

  const syncGateMarkers = () => {
    if(!map ||!estimate.gates) return;

    const gateIdToLocalIdMap = buildElementIdToLocalIdMap(estimate.gates);
    setGateIdToLocalIdMap(gateIdToLocalIdMap);

    syncMarkers(
      document,
      map,
      mapScale,
      'gate',
      estimate.gates,
      gateMarkersRef,
      setGateMarkers,
      gate => `${gateIdToLocalIdMap[gate.id]}`,
      _ => (<Fence fontSize={'large'}/>),
      (gate) => {
        setSelectedGate(gate);
      },
      (gate, event) => {
        const position = {lat: event.latLng?.lat(), lng: event.latLng?.lng()};
        updateGateLocation(gate, position.lat, position.lng);
      },
      {gmpDraggable: canEdit}
    )
  }

  const syncAdditionalComponentMarkers = () => {
    if(!map ||!estimate.additionalComponents) return;

    const chipperIdToLocalIdMap = buildElementIdToLocalIdMap(estimate.additionalComponents.filter(ac => ac.type === 'chipper'));
    setChipperIdToLocalIdMap(chipperIdToLocalIdMap);

    syncMarkers(
      document,
      map,
      mapScale,
      'additional-component',
      estimate.additionalComponents,
      additionalComponentMarkersRef,
      setAdditionalComponentMarkers,
      additionalComponent => `Truck & Chipper ${chipperIdToLocalIdMap[additionalComponent.id]}`,
      additionalComponent => {
        // The icon is drawn front-up, so its height is the vehicle's length and its
        // width follows the asset's proportions.
        const iconLengthPixels = getChipperIconLengthPixels(additionalComponent.lat, ICON_STANDARD_ZOOM_LEVEL);
        const iconScale = getChipperIconScale(mapScale);
        return (
          <MapImageIcon
            id={`chipper-marker-${additionalComponent.id}`}
            src={truckChipperIcon}
            sizeX={iconLengthPixels * CHIPPER_ICON_WIDTH_TO_LENGTH_RATIO}
            sizeY={iconLengthPixels}
            scale={iconScale}
            label={`${chipperIdToLocalIdMap[additionalComponent.id] ?? ''}`}
            labelStyle={{
              color: '#ffffff',
              fontWeight: 800,
              fontSize: Math.max(11, iconLengthPixels * iconScale * 0.16),
              lineHeight: 1,
              // The number sits on top of the vehicle art, so it carries its own contrast.
              textShadow: '0 0 3px rgba(0,0,0,0.95), 0 1px 2px rgba(0,0,0,0.95)',
            }}
            centerImage={true}
            rotationDeg={additionalComponent.heading}
            showRotationHandle={canEdit}
            preserveAspectRatio
          />
        );
      },
      (element) => onAdditionComponentMarkerClick(element),
      (element, event) => {
        const position = {lat: event.latLng?.lat(), lng: event.latLng?.lng()};
        updateAdditionalComponentLocation(element, position.lat, position.lng);
      },
      {gmpDraggable: canEdit},
      undefined,
      additionalComponent => `${additionalComponent.heading}`
    )
  }

  const onZoomChanged = (map: google.maps.Map) => {
    const zoom = map.getZoom()!;
    const scale = Math.pow(2, zoom - ICON_STANDARD_ZOOM_LEVEL);
    setMapScale(scale);
  }

  // Initial page load
  useEffect(() => {
    if(!mapRef.current) return;

    if (initialTreeId) {
      const treeToSelect = estimate.trees.find(t => t.id === initialTreeId);
      if (treeToSelect) {
        setSelectedTree(treeToSelect);
      } else {
        logError(`Deep link failed: Tree with id ${initialTreeId} not found in estimate ${estimate.id}`);
        enqueueSnackbar(`Tree with id ${initialTreeId} not found.`, { variant: 'warning' });
      }
    }

    const loader = googleMapsLoader();
    loader.importLibrary('core').then(async () => {
      const { Map } = await loader.importLibrary("maps") as google.maps.MapsLibrary;
      await loader.importLibrary('marker');
      await loader.importLibrary('geometry');

      const center = centerOnUser && userLocation ? userLocation : {lat: estimate.lat, lng: estimate.lng};
      const maxZoom = await getMaxZoom(center.lat, center.lng);
      const initialZoom = Math.max(maxZoom - 1, DEFAULT_ZOOM_LEVEL);

      if(!mapRef.current) return; // On iOS, it seems the mapRef can get set to null in between the check at the top of this useEffect and here
      const map = new Map(mapRef.current!, {
        mapId: 'ESTIMATE_MAP',
        center: center,
        zoom: initialZoom,
        maxZoom: maxZoom,
        mapTypeId: 'satellite',
        tilt: 0,
        streetViewControl: false,
        zoomControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        scaleControl: false,
        rotateControl: false,
        clickableIcons: false,
      });
      map.addListener('zoom_changed', () => onZoomChanged(map));
      setMap(map);
      setPageEssentialsLoaded(true);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  useEffect(() => {
    syncTreeMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, estimate.trees, canEdit])

  // Projection is often null until the map finishes initializing; re-sync for pixel clustering.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('projection_changed', () => {
      syncTreeMarkers();
    });
    return () => {
      google.maps.event.removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  useEffect(() => {
    return () => {
      clearSpiderLines();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    syncGateMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, estimate.gates, canEdit])

  useEffect(() => {
    syncAdditionalComponentMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, estimate.additionalComponents, canEdit]);

  // Truck & chipper rotation gesture: a small grab handle rendered on the icon (see
  // MapImageIcon's rotation handle) drives rotation directly via the DOM, independent of
  // the marker's own move-drag. Delegated at the document level so it keeps working across
  // marker content re-renders without per-marker listener bookkeeping.
  useEffect(() => {
    if (!canEdit) return;

    const activeRotation: {chipperId: number | null, center: {x: number, y: number} | null, handleEl: HTMLElement | null, rotatorEl: HTMLElement | null} = {
      chipperId: null,
      center: null,
      handleEl: null,
      rotatorEl: null,
    };

    const parseChipperIdFromHandleId = (elementId: string | undefined): number | null => {
      const match = elementId?.match(/^chipper-marker-(\d+)-rotate-handle$/);
      return match ? parseInt(match[1], 10) : null;
    };

    const getChipperMarkerCenterScreenPoint = (chipperId: number): {x: number, y: number} | null => {
      const markerElement = additionalComponentMarkersRef.current.find(m => m.elementId === chipperId)?.markerDomObject.element;
      if (!markerElement) return null;
      const rect = markerElement.getBoundingClientRect();
      return {x: rect.left + rect.width / 2, y: rect.top + rect.height / 2};
    };

    const computeHeadingDeg = (center: {x: number, y: number}, pointer: {x: number, y: number}) => {
      const dx = pointer.x - center.x;
      const dy = pointer.y - center.y;
      return (Math.atan2(dx, -dy) * (180 / Math.PI) + 360) % 360;
    };

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement;
      const chipperId = parseChipperIdFromHandleId(target?.id);
      if (chipperId == null) return;
      const center = getChipperMarkerCenterScreenPoint(chipperId);
      if (!center) return;

      // Prevent this gesture from also being interpreted as a marker move-drag or click.
      event.stopPropagation();
      event.preventDefault();

      // The handle sits at a fixed position; the sibling <img> is what actually rotates.
      const rotatorEl = target.parentElement?.querySelector('img') as HTMLElement | null;

      activeRotation.chipperId = chipperId;
      activeRotation.center = center;
      activeRotation.handleEl = target;
      activeRotation.rotatorEl = rotatorEl;
      target.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (activeRotation.chipperId == null || !activeRotation.center || !activeRotation.rotatorEl) return;
      event.stopPropagation();
      event.preventDefault();
      const heading = computeHeadingDeg(activeRotation.center, {x: event.clientX, y: event.clientY});
      activeRotation.rotatorEl.style.transform = `rotate(${heading}deg)`;
    };

    const onPointerUp = (event: PointerEvent) => {
      if (activeRotation.chipperId == null || !activeRotation.center) return;
      event.stopPropagation();
      event.preventDefault();
      const heading = computeHeadingDeg(activeRotation.center, {x: event.clientX, y: event.clientY});
      const chipperId = activeRotation.chipperId;
      activeRotation.handleEl?.releasePointerCapture(event.pointerId);
      activeRotation.chipperId = null;
      activeRotation.center = null;
      activeRotation.handleEl = null;
      activeRotation.rotatorEl = null;

      const chipper = changedEstimateRef.current.additionalComponents.find(ac => ac.id === chipperId);
      if (chipper) {
        updateChipperHeading(chipper, heading);
      }
    };

    document.addEventListener('pointerdown', onPointerDown, {capture: true});
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, {capture: true});
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, canEdit]);

  useEffect(() => {
    if(!pageEssentialsLoaded) return;
    syncTreeMarkers();
    syncGateMarkers();
    syncAdditionalComponentMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapScale, canEdit]);


  // User marker updates
  useEffect(() => {
    if(!map) return;
    if(userMarker)
    {
      userMarker.map = null;
    }
    if (!userLocation || !hasLocationPermission) return;

    const content = renderReactNode(<MapImageIcon src={personIcon} sizeX={64} sizeY={64} centerImage={true}/>);
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: userLocation,
      map: map,
      content
    });
    setUserMarker(marker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, userLocation, hasLocationPermission]);

  const onPathChanged_Callback = (pathId: number, path: google.maps.MVCArray<google.maps.LatLng>, changedPointIndex: number) => {
    const dragPathUpdate = getDragPathUpdate(changedEstimateRef.current!, path, pathId, changedPointIndex);
    updateDragPath(navigate, estimate.id, pathId, dragPathUpdate)
      .then(updatedPath => {
        setEstimate(e => ({
          ...e,
          dragPaths: e.dragPaths.map(d => d.id === pathId ? {...d, ...dragPathUpdate} : d),
          trees: replaceItemInArray(e.trees, t => t.id === updatedPath.treeId, t => ({...t, dragDistanceFeet: updatedPath.distanceFeet}))
        }));
        bumpMarkerZIndexes(); // This is to ensure the markers are drawn over the drag paths
      })
      .catch(e => {
        logError('Error updating drag path', {}, e);
        enqueueSnackbar(`Error updating drag path: ${e}`, {variant: 'error', autoHideDuration: 5000});
      });
  };

  // Drag path updates
  useEffect(() => {
    if(!map) return;
    const pathsToRemove = dragPathsObjects.filter(dp => !estimate.dragPaths.some(d => d.id === dp.pathId));
    const pathsToKeep = dragPathsObjects.filter(dp => estimate.dragPaths.some(d => d.id === dp.pathId));
    const pathsNotOnMap = estimate.dragPaths.filter(d => !dragPathsObjects.some(dp => dp.pathId === d.id)) ?? [];

    const pathsToAppend = pathsNotOnMap.map(dp => {
      const path = new google.maps.Polyline({
        path: dp.points.map(p => new google.maps.LatLng(p.lat, p.lng)),
        geodesic: true,
        strokeColor: 'red',
        strokeOpacity: 1.0,
        editable: canEdit,
        strokeWeight: 2,
        map: map,
      });
      if (canEdit) {
        google.maps.event.addListener(path.getPath(), 'insert_at', (newPointIndex: number) => onPathChanged_Callback(dp.id, path.getPath(), newPointIndex));
        google.maps.event.addListener(path.getPath(), 'set_at', (setPointIndex: number) => onPathChanged_Callback(dp.id, path.getPath(), setPointIndex));
      }
      return {pathId: dp.id, path};
    });
    const pathUpdatePromises = pathsToKeep.map(async pathToKeep => {
      pathToKeep.path.setOptions({ editable: canEdit });

      // We do this check every time because state updates to canEdit do not propogate to the map, and it's easier to clear the listeners each time than to track them all individually
      if(canEdit) {
        google.maps.event.clearListeners(pathToKeep.path.getPath(), 'insert_at');
        google.maps.event.clearListeners(pathToKeep.path.getPath(), 'set_at');
        google.maps.event.addListener(pathToKeep.path.getPath(), 'insert_at', (newPointIndex: number) => onPathChanged_Callback(pathToKeep.pathId, pathToKeep.path.getPath(), newPointIndex));
        google.maps.event.addListener(pathToKeep.path.getPath(), 'set_at', (setPointIndex: number) => onPathChanged_Callback(pathToKeep.pathId, pathToKeep.path.getPath(), setPointIndex));
      }

      const estimatePath = estimate.dragPaths.find(d => d.id === pathToKeep.pathId)!;
      const estimatePathPoints = estimatePath.points.map(p => new google.maps.LatLng(p.lat, p.lng));
      const pathsMatch = estimatePathPoints.length === pathToKeep.path.getPath().getLength() &&
        pathToKeep.path.getPath().getArray().every((p, i) => p.lat() === estimatePathPoints[i].lat() && p.lng() === estimatePathPoints[i].lng());
      if(!pathsMatch) {
        const newPathPoints = new google.maps.MVCArray(estimatePathPoints);
        if (canEdit) {
          google.maps.event.addListener(newPathPoints, 'insert_at', (newPointIndex: number) => onPathChanged_Callback(estimatePath.id, newPathPoints, newPointIndex));
          google.maps.event.addListener(newPathPoints, 'set_at', (setPointIndex: number) => onPathChanged_Callback(estimatePath.id, newPathPoints, setPointIndex));
        }
        pathToKeep.path.setPath(newPathPoints);
        pathToKeep.path.setOptions({ editable: canEdit });
        await updateDragPath(navigate, estimate.id, pathToKeep.pathId, {
          points: estimatePath.points,
          distanceFeet: estimatePath.distanceFeet
        });
        setDragPathsObjects(prevDragPaths => prevDragPaths.map(dp => dp.pathId === pathToKeep.pathId ? pathToKeep : dp));
      }
    });
    Promise.all(pathUpdatePromises)
      .then(() => {
        pathsToRemove.forEach(p => p.path.setMap(null));
        setDragPathsObjects([...pathsToKeep, ...pathsToAppend]);
        bumpMarkerZIndexes(); // This is to ensure the markers are drawn over the drag paths
      })
      .catch(e => {
        logError('Error updating drag paths', {}, e);
        enqueueSnackbar(`Error updating drag paths: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, estimate.dragPaths, canEdit]);

  // Center on user
  useEffect(() => {
    if (!map) return;

    if(centerOnUser && userLocation && hasLocationPermission) {
      map.setCenter(userLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation, centerOnUser, hasLocationPermission]);

  async function continuousUserLocationUpdate() {
    const location = await requestLocation(false);
    if (location) setUserLocation(location);
  }

  useEffect(() => {
    if (!hasLocationPermission) {
      setCenterOnUser(false);
      setUserLocation(null);
      return;
    }
    let cancelled = false;
    requestLocation().then(location => { if (!cancelled) setUserLocation(location); });
    return () => { cancelled = true; };
  }, [hasLocationPermission, requestLocation]);

  // Only poll while we actually have permission; otherwise the request is
  // guaranteed to fail and would spam errors / waste battery.
  useInterval(continuousUserLocationUpdate, hasLocationPermission ? POSITION_UPDATE_INTERVAL_MS : null);

  const toggleCenterOnUser = async () => {
    if(centerOnUser)
    {
      enqueueSnackbar('Centering on user disabled, map is unlocked', {variant: 'info', autoHideDuration: 5000})
    } else {
      const location = await requestLocation();
      if (!location) {
        enqueueSnackbar('Location unavailable. You can still use the map.', {variant: 'info'});
        return;
      }
      setUserLocation(location);
      enqueueSnackbar('Centering on user enabled, map will follow your location', {variant: 'info', autoHideDuration: 5000})
    }
    setCenterOnUser(!centerOnUser);
  }

  const createDragPaths = async (chipperId: number, chipperLocation: {lat: number, lng: number}, trees: EstimateTreeDto[]) => {
    const createPathPromises = trees.map(tree => {
      const points = [{lat: tree.lat, lng: tree.lng}, chipperLocation];
      const distanceFeet = getPathDistanceFeet(points);
      const dragPath: AddEstimateDragPathDto = {
        estimateId: estimate.id,
        treeId: tree.id,
        chipperTruckId: chipperId,
        points: [{lat: tree.lat, lng: tree.lng}, chipperLocation],
        distanceFeet
      };
      return createDragPath(navigate, estimate.id, dragPath);
    });
    // Errors are intentionally left to propagate: the only caller (handleSaveChipperConnections)
    // needs to see them to avoid treating a partially-failed save as a success.
    const createdPaths = await Promise.all(createPathPromises);
    setEstimate(e => {
      const updatedTrees = e.trees.map(t => ({
        ...t,
        dragDistanceFeet: createdPaths.find(dp => dp.treeId === t.id)?.distanceFeet ?? t.dragDistanceFeet
      }));
      const newEstimate = {...e, dragPaths: [...(e.dragPaths ?? []), ...createdPaths], trees: updatedTrees};
      return newEstimate;
    });
  }

  const getOccupiedLocations = () => [
    ...estimate.trees,
    ...estimate.gates,
    ...estimate.additionalComponents,
  ].map(item => ({lat: item.lat, lng: item.lng}));

  const getNextSpawnLocation = () => nextOpenLocation(
    {lat: estimate.lat, lng: estimate.lng},
    getOccupiedLocations()
  );

  const addWorkItem = async (workItemType: WorkItemType) => {
    setCreateItemIsLoading(true);
    const treeLocation = getNextSpawnLocation();
    await createTree(navigate, estimate.id, {workItemType, lat: treeLocation.lat, lng: treeLocation.lng})
      .then(async createdTree => {
        setEstimate(e => {
          const newEstimate = {...e, trees: [...e.trees, createdTree]};
          console.log('addWorkItem.newEstimate', newEstimate);
          return newEstimate;
        });
        setSelectedTree(createdTree);
        // Work items no longer auto-connect to a chipper truck — the user manually
        // associates them from within the Truck & Chipper modal.
      })
      .catch(e => {
        logError('Error creating tree', {}, e)
        enqueueSnackbar(`Error creating tree: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => setCreateItemIsLoading(false));
  }

  const addGate = async () => {
    setCreateItemIsLoading(true);
    const gateLocation = getNextSpawnLocation();
    await createGate(navigate, estimate.id, {lat: gateLocation.lat, lng: gateLocation.lng})
      .then(createdGate => {
        setEstimate(e => ({...e, gates: [...e.gates, createdGate]}));
        setSelectedGate(createdGate);
      })
      .catch(e => {
        logError('Error creating gate', {}, e)
        enqueueSnackbar(`Error creating gate: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => setCreateItemIsLoading(false));
  }

  const addChipperTruck = async () => {
    setCreateItemIsLoading(true);
    const location = getNextSpawnLocation();
    await createAdditionalComponent(navigate, estimate.id, {lat: location.lat, lng: location.lng, type: 'chipper'})
      .then(createdChipperTruck => {
        setEstimate(e => {
          return {...e, additionalComponents: [...e.additionalComponents, createdChipperTruck]}
        });
        // No auto-connect on placement — work items are manually associated from the modal.
        setSelectedChipperTruck(createdChipperTruck);
      })
      .catch(e => {
        logError('Error creating chipper', {}, e)
        enqueueSnackbar(`Error creating chipper: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => setCreateItemIsLoading(false));
  }

  // A request made before this map mounted belongs to an earlier visit.
  const handledTruckRequest = useRef(truckRequest?.id);
  useEffect(() => {
    if (!truckRequest || truckRequest.id === handledTruckRequest.current) return;
    handledTruckRequest.current = truckRequest.id;
    const trucks = estimate.additionalComponents.filter(component => component.type === 'chipper');
    const truck = trucks.find(component => component.id === truckRequest.truckId) ?? trucks[0];
    if (truckRequest.action === 'add') void addChipperTruck();
    else if (truck && truckRequest.action === 'edit') setSelectedChipperTruck(truck);
    else if (truck && truckRequest.action === 'show') map?.panTo({lat: truck.lat, lng: truck.lng});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckRequest?.id]);

  const updateTreeLocation = async (tree: EstimateTreeDto, lat: number | undefined, lng: number | undefined) => {
    if(!lat || !lng) return;

    const updatedTree = {
      ...tree,
      lat,
      lng
    };
    const updateDto = {
      ...updatedTree,
      treeId: tree.id,
      imageIds: tree.images?.map(i => i.id) ?? [],
      updatedBy: tree.createdBy,
    }
    setEstimate(e => ({...e, trees: e.trees.map(t => t.id === tree.id ? updatedTree : t)}));
    await updateTree(navigate, estimate.id, tree.id, updateDto)
      .then(() => {
        updateDragPathStart(tree.id, {lat, lng});
      })
      .catch(e => {
        logError('Error moving tree', {}, e)
        enqueueSnackbar(`Error moving tree, please refresh: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
  }

  const updateGateLocation = async (gate: EstimateGateDetailsDto, lat: number | undefined, lng: number | undefined) => {
    if(!lat || !lng) return;

    const updatedGate = {
      ...gate,
      lat,
      lng
    };
    const updateDto = {
      ...updatedGate,
      gateId: gate.id,
      imageIds: gate.images?.map(i => i.id) ?? [],
      updatedBy: gate.createdBy,
    }
    setEstimate(e => ({...e, gates: e.gates.map(t => t.id === gate.id ? updatedGate : t)}));
    await updateGate(navigate, estimate.id, gate.id, updateDto)
      .catch(e => {
        logError('Error moving gate', {}, e)
        enqueueSnackbar(`Error moving gate, please refresh: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
  }

  const updateDragPathStart = (treeId: number, newTreeLocation: {lat: number, lng: number}) => {
    setEstimate(e =>  {
      const pathToUpdate = e.dragPaths.find(dp => dp.treeId === treeId);
      if(!pathToUpdate) {
        return e;
      }
      const points = [newTreeLocation, ...pathToUpdate.points.slice(1, pathToUpdate.points.length)];
      const distanceFeet = getPathDistanceFeet(points);
      const updatedPath = {...pathToUpdate, points, distanceFeet};
      const updatedTrees = replaceItemInArray(e.trees, t => t.id === treeId, t => ({...t, dragDistanceFeet: distanceFeet}));
      return {...e, dragPaths: e.dragPaths.map(dp => dp.id === updatedPath.id ? updatedPath : dp), trees: updatedTrees};
    });
  }

  // Recomputes every dragline ending at this chipper's rear-center anchor — called after
  // the chipper is moved or rotated, since both change where that anchor point sits.
  const updateDragPathsEndsForChipper = (chipper: {id: number, lat: number, lng: number, heading: number}) => {
    const anchor = getChipperRearCenterAnchor(chipper);

    // First, update the state with the new paths
    setEstimate(e => {
      const pathsToUpdate = e.dragPaths.filter(dp => dp.chipperTruckId === chipper.id);
      const pathsNotToUpdate = e.dragPaths.filter(dp => dp.chipperTruckId !== chipper.id);
      const updatedPaths = pathsToUpdate.map(dp => {
        const points = [...dp.points.slice(0, dp.points.length - 1), anchor];
        const distanceFeet = getPathDistanceFeet(points);
        const updatedPath = {...dp, points, distanceFeet};
        return updatedPath;
      });
      const treesNotToUpdate = e.trees.filter(t => !updatedPaths.some(dp => dp.treeId === t.id));
      const updatedTrees = updatedPaths.map(dp => ({...e.trees.find(t => t.id === dp.treeId)!, dragDistanceFeet: dp.distanceFeet}));

      return {...e, dragPaths: [...pathsNotToUpdate, ...updatedPaths], trees: [...treesNotToUpdate, ...updatedTrees]};
    });

    setEstimate(e => {
      const pathsToUpdate = e.dragPaths.filter(dp => dp.chipperTruckId === chipper.id);
      const updatePromises = pathsToUpdate.map(dp => updateDragPath(navigate, estimate.id, dp.id, dp));

      Promise.all(updatePromises)
        .catch(error => {
          console.error("Failed to update drag paths:", error);
        });

      // Return the state unchanged - we already updated it in the previous call
      return e;
    });
  };

  const updateChipperHeading = async (chipper: EstimateAdditionalComponentDto, headingDeg: number) => {
    const normalizedHeading = ((headingDeg % 360) + 360) % 360;
    const updatedChipper = {...chipper, heading: normalizedHeading};
    const updateDto = {
      ...updatedChipper,
      imageIds: chipper.images?.map(i => i.id) ?? [],
    };
    setEstimate(e => ({...e, additionalComponents: e.additionalComponents.map(ac => ac.id === chipper.id ? updatedChipper : ac)}));
    await updateAdditionalComponent(navigate, estimate.id, chipper.id, updateDto)
      .then(component => {
        updateDragPathsEndsForChipper({id: component.id, lat: component.lat, lng: component.lng, heading: component.heading});
      })
      .catch(e => {
        logError('Error rotating chipper', {}, e);
        enqueueSnackbar(`Error rotating chipper, please refresh: ${e}`, {variant: 'error', autoHideDuration: 5000});
      });
  };

  const updateAdditionalComponentLocation = async (additionalComponent: EstimateAdditionalComponentDto, lat: number | undefined, lng: number | undefined) => {
    if(!lat || !lng) return;

    const updatedAdditionalComponent = {
      ...additionalComponent,
      lat,
      lng
    };
    const updateDto = {
      ...updatedAdditionalComponent,
      imageIds: additionalComponent.images?.map(i => i.id) ?? [],
    }
    setEstimate(e => ({...e, additionalComponents: e.additionalComponents.map(t => t.id === additionalComponent.id ? updatedAdditionalComponent : t)}));
    await updateAdditionalComponent(navigate, estimate.id, additionalComponent.id, updateDto)
      .then(component => {
        if(component.type === 'chipper') {
          updateDragPathsEndsForChipper({id: component.id, lat: component.lat, lng: component.lng, heading: component.heading});
        }
      })
      .catch(e => {
        logError('Error moving chipper', {}, e)
        enqueueSnackbar(`Error moving chipper, please refresh: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
  }

  const handleGateDelete = (deletedGate: EstimateGateDto) => {
    setEstimate(e => ({...e, gates: e.gates.filter(t => t.id !== deletedGate.id)}));
    setSelectedGate(undefined);
  }

  const handleNewWorkItemFromWorkItem = async (updatedTree: EstimateTreeDto, workItemType: WorkItemType) => {
    setEstimate(e => ({...e, trees: e.trees.map(t => t.id === updatedTree.id ? updatedTree : t)}));
    await addWorkItem(workItemType);
  }

  const handleNewWorkItemFromGate = async (updatedGate: EstimateGateDetailsDto, workItemType: WorkItemType) => {
    setEstimate(e => ({...e, gates: e.gates.map(t => t.id === updatedGate.id ? updatedGate : t)}));
    clearGateSelection();
    await addWorkItem(workItemType);
  }

  const handleNewGateFromWorkItem = async (updatedTree: EstimateTreeDto) => {
    setEstimate(e => ({...e, trees: e.trees.map(t => t.id === updatedTree.id ? updatedTree : t)}));
    clearTreeSelection();
    await addGate();
  }

  const handleNewGateFromGate = async (updatedGate: EstimateGateDetailsDto) => {
    setEstimate(e => ({...e, gates: e.gates.map(t => t.id === updatedGate.id ? updatedGate : t)}));
    await addGate();
  }

  const handleSaveAndReturnToEstimate = (updatedTree: EstimateTreeDto) => {
    setEstimate(e => ({...e, trees: e.trees.map(t => t.id === updatedTree.id ? updatedTree : t)}));
    setSelectedTree(undefined); // Close the modal
  }

  const handleTreeDelete = (deletedTree: EstimateTreeDto) => {
    const dragPath = estimate.dragPaths.find(dp => dp.treeId === deletedTree.id);
    const dragPathDeletionPromise = dragPath ? deleteDragPath(navigate, estimate.id, dragPath.id) : Promise.resolve();
    dragPathDeletionPromise
      .then(() => {
        setEstimate(e => (
          {...e,
            trees: e.trees.filter(t => t.id !== deletedTree.id),
            dragPaths: e.dragPaths.filter(dp => dp.id !== (dragPath?.id ?? -1))
          }));
      })
      .catch(e => {
        logError('Error deleting drag path', {}, e)
        enqueueSnackbar(`Error deleting drag path: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => {
        setSelectedTree(undefined);
      })
  }

  const handleReturnToEstimateFromGate = async (updateGate: EstimateGateDetailsDto) => {
    setEstimate(e => ({...e, gates: e.gates.map(t => t.id === updateGate.id ? updateGate : t)}));
    setSelectedGate(undefined); // Close the modal
  }

  const clearTreeSelection = () => {
    if (selectedTree) {
      const localStorageKey = getUnsavedModificationsLocalStorageKey(estimate.id, selectedTree.id);
      try {
        localStorage.removeItem(localStorageKey);
        console.log(`Cleared localStorage for tree ${selectedTree.id} due to modal close.`);
      } catch (error) {
        const errorToLog = error instanceof Error ? error : new Error(String(error));
        logError('Error removing tree state from localStorage on close', { key: localStorageKey }, errorToLog);
      }
    }

    setSelectedTree(undefined);
    navigate(urls.getBaseUrl(), { replace: true });
  }

  const clearGateSelection = () => {
    setSelectedGate(undefined);
  }

  const handleTreeClick = (treeId: number) => {
    console.log('handleTreeClick', treeId);
    if (onWorkItemSelect) {
      onWorkItemSelect(treeId);
      return;
    }
    const tree = estimate.trees.find(t => t.id === treeId);
    if (tree) {
      setSelectedTree(tree);
      navigate(urls.getSelectedTreeUrl(treeId), { replace: true });
    } else {
      logError(`handleTreeClick: Tree with id ${treeId} not found`);
    }
  }

  // Delete Truck & Chipper removes the truck and all associated draglines outright — no
  // reassignment to another chipper.
  const handleChipperTruckDelete = async (deletedChipperTruck: EstimateAdditionalComponentDto) => {
    const attachedDragPaths = estimate.dragPaths.filter(dp => dp.chipperTruckId === deletedChipperTruck.id) ?? [];
    await Promise.all(attachedDragPaths.map(dp => deleteDragPath(navigate, estimate.id, dp.id)))
      .then(() => {
        const updatedTrees = estimate.trees.map(t =>
          attachedDragPaths.some(dp => dp.treeId === t.id) ? {...t, dragDistanceFeet: undefined} : t
        );
        setEstimate(e => ({
          ...e,
          additionalComponents: e.additionalComponents.filter(t => t.id !== deletedChipperTruck.id),
          dragPaths: e.dragPaths.filter(dp => dp.chipperTruckId !== deletedChipperTruck.id),
          trees: updatedTrees
        }));
      })
      .catch(e => {
        logError('Error deleting truck & chipper', {}, e);
        enqueueSnackbar(`Error deleting truck & chipper: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => {
        setSelectedChipperTruck(undefined);
      });
  }

  const handleChipperTruckDetailCancel = () => {
    setSelectedChipperTruck(undefined);
  }

  // Diffs the checked work items against the chipper's current draglines and creates/deletes
  // as needed, anchoring any new draglines to the chipper's rear-center (chipping) point.
  const handleSaveChipperConnections = async (chipper: EstimateAdditionalComponentDto, selectedTreeIds: number[]) => {
    const currentTreeIds = estimate.dragPaths.filter(dp => dp.chipperTruckId === chipper.id).map(dp => dp.treeId);
    const treeIdsToAdd = selectedTreeIds.filter(id => !currentTreeIds.includes(id));
    const treeIdsToRemove = currentTreeIds.filter(id => !selectedTreeIds.includes(id));

    const pathsToRemove = estimate.dragPaths.filter(dp => dp.chipperTruckId === chipper.id && treeIdsToRemove.includes(dp.treeId));
    const treesToAdd = estimate.trees.filter(t => treeIdsToAdd.includes(t.id));

    try {
      if (pathsToRemove.length > 0) {
        await Promise.all(pathsToRemove.map(dp => deleteDragPath(navigate, estimate.id, dp.id)));
        setEstimate(e => ({
          ...e,
          dragPaths: e.dragPaths.filter(dp => !pathsToRemove.some(p => p.id === dp.id)),
          trees: e.trees.map(t => treeIdsToRemove.includes(t.id) ? {...t, dragDistanceFeet: undefined} : t)
        }));
      }

      if (treesToAdd.length > 0) {
        const anchor = getChipperRearCenterAnchor(chipper);
        await createDragPaths(chipper.id, anchor, treesToAdd);
      }
    } catch (e) {
      logError('Error connecting work items to truck & chipper', {}, e as Error);
      enqueueSnackbar(`Error connecting work items to truck & chipper: ${e}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setSelectedChipperTruck(undefined);
    }
  }

  return (
    <div style={{width: '100%', height: '100%', margin: 0}}>
      <div style={{position: 'relative', width: '100%', height: '100%'}}>
        <div id="map" style={{height: '100%'}} ref={mapRef}></div>
        <EstimateStatusUpdateMenu estimateId={estimate.id} lastButtonClickEvent={updateStatusMenuLastClick}/>
        <div className="estimate-control-overlay">
          {sidebarEnabled && <EstimateInfoSidebar estimate={estimate} treeIdToLocalIdMap={treeIdToLocalIdMap} onTreeClick={handleTreeClick} startOpen={true} />}
        </div>
        <div className="estimate-control-overlay bottom-controls-container">
          <div className="bottom-right-element-control-container ">
            <div className="center-on-user-fab-container">
              <Tooltip title={locationState === 'denied' ? 'Location is off for this site. Enable it in your browser to center on you.' : locationState === 'unsupported' ? 'This browser does not support location.' : 'Center on me'}>
                <span>
                  <Fab className="overlaid-control" color="info" onClick={toggleCenterOnUser} aria-label="Center on me" aria-pressed={centerOnUser} disabled={locationState === 'denied' || locationState === 'unsupported'}>
                    {centerOnUser ? <MyLocation fontSize="large"/> : <LocationSearching fontSize="large"/>}
                  </Fab>
                </span>
              </Tooltip>
            </div>
            {addingEnabled && <div className="add-element-speed-dial-container">
              <SpeedDial 
                className="overlaid-control" 
                color="primary" 
                ariaLabel={"Add"}  
                icon={createItemIsLoading ? <CircularProgress color="secondary"/> : <AddIcon fontSize="large"/>} 
                direction={"up"}
                open={canEdit ? speedDialOpen : false}
                onClose={() => setSpeedDialOpen(false)}
                onOpen={() => canEdit && setSpeedDialOpen(true)}
                sx={!canEdit ? { 
                  '& .MuiFab-root': { 
                    color: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(50, 50, 50, 1)'
                  } 
                } : {}}
              >
                <SpeedDialAction onClick={() => addWorkItem(WorkItemType.Tree)} className="overlaid-control" color="primary" key={'addTree'} icon={<img src={treeIcon} alt="Tree" style={{width: 24, height: 24}} />} tooltipTitle={'Add Tree'}/>
                <SpeedDialAction onClick={() => addWorkItem(WorkItemType.TreeGroup)} className="overlaid-control" color="primary" key={'addTreeGroup'} icon={<img src={treeGroupIcon} alt="Tree Group" style={{width: 24, height: 24}} />} tooltipTitle={'Add Tree Group'}/>
                <SpeedDialAction onClick={() => addWorkItem(WorkItemType.FallenTree)} className="overlaid-control" color="primary" key={'addFallenTree'} icon={<img src={fallenTreeIcon} alt="Fallen Tree" style={{width: 24, height: 24}} />} tooltipTitle={'Add Fallen Tree'}/>
                <SpeedDialAction onClick={() => addWorkItem(WorkItemType.Stump)} className="overlaid-control" color="primary" key={'addStump'} icon={<img src={stumpIcon} alt="Stump" style={{width: 24, height: 24}} />} tooltipTitle={'Add Stump'}/>
                <SpeedDialAction onClick={() => addWorkItem(WorkItemType.DebrisPile)} className="overlaid-control" color="primary" key={'addDebrisPile'} icon={<img src={debrisPileIcon} alt="Debris Pile" style={{width: 24, height: 24}} />} tooltipTitle={'Add Debris Pile'}/>
                <SpeedDialAction onClick={() => addWorkItem(WorkItemType.Misc)} className="overlaid-control" color="primary" key={'addMisc'} icon={<img src={miscellaneousIcon} alt="Miscellaneous" style={{width: 24, height: 24}} />} tooltipTitle={'Add Miscellaneous'}/>
                <SpeedDialAction onClick={addGate} className="overlaid-control" color="primary" key={'addGate'} icon={<Fence/>} tooltipTitle={'Add Gate'}/>
                <SpeedDialAction onClick={addChipperTruck} className="overlaid-control" color="primary" key={'addAdditionalComponent'} icon={<LocalShipping/>} tooltipTitle={'Add Chipper Truck'}/>
              </SpeedDial>
            </div>}
          </div>
        </div>
      </div>
      <Modal open={!!selectedTree} onClose={clearTreeSelection}>
        <EstimateTree
          tree={selectedTree!}
          treeLocalId={treeIdToLocalIdMap[selectedTree?.id ?? -1]}
          company={company!}
          onNewWorkItem={handleNewWorkItemFromWorkItem}
          onNewGate={handleNewGateFromWorkItem}
          handleSaveAndReturnToEstimate={handleSaveAndReturnToEstimate}
          onTreeDelete={() => handleTreeDelete(selectedTree!)}
          mode={mode}
        />
      </Modal>
      <Modal open={!!selectedGate} onClose={clearGateSelection}>
        <EstimateGateDetail
          estimateId={estimate.id}
          estimateTrees={estimate.trees ?? []}
          treeLocalIdMap={treeIdToLocalIdMap}
          gateId={selectedGate?.id ?? -1}
          gateLocalId={gateIdToLocalIdMap[selectedGate?.id ?? -1]}
          onNewWorkItem={handleNewWorkItemFromGate}
          onNewGate={handleNewGateFromGate}
          onReturnToEstimate={handleReturnToEstimateFromGate}
          onGateDelete={() => handleGateDelete(selectedGate!)}
          mode={mode}/>
      </Modal>
      <Modal open={!!selectedChipperTruck} onClose={handleChipperTruckDetailCancel}>
        <EstimateChipperTruckDetailComponent
          chipperTruck={selectedChipperTruck!}
          chipperLocalId={chipperIdToLocalIdMap[selectedChipperTruck?.id ?? -1]}
          estimateTrees={estimate.trees ?? []}
          treeLocalIdMap={treeIdToLocalIdMap}
          connectedTreeIds={estimate.dragPaths.filter(dp => dp.chipperTruckId === selectedChipperTruck?.id).map(dp => dp.treeId)}
          onSave={(chipper, selectedTreeIds) => handleSaveChipperConnections(chipper, selectedTreeIds)}
          onDelete={() => handleChipperTruckDelete(selectedChipperTruck!)}
          onCancel={handleChipperTruckDetailCancel}
          mode={mode}/>
      </Modal>
    </div>
  );
};

export default EstimateMapWorkspaceComponent;
